/**
 * Server Action Error Handler
 *
 * Handles Server Action mismatch errors that occur when cached client code
 * tries to call Server Actions that don't exist in the new deployment.
 *
 * This utility provides automatic recovery by refreshing the page once,
 * with safeguards against infinite refresh loops.
 */

const REFRESH_COUNT_KEY = "serverActionRefreshCount";
const REFRESH_TIMESTAMP_KEY = "serverActionRefreshTimestamp";
const MAX_REFRESH_ATTEMPTS = 2;
const REFRESH_TIMEOUT_MS = 60000; // 1 minute

function getErrorMessage(error: unknown): string {
  if (error instanceof Error) {
    return error.message;
  }

  if (typeof error === "string") {
    return error;
  }

  if (typeof error === "object" && error !== null) {
    const errorObj = error as Record<string, unknown>;
    if (typeof errorObj.message === "string") {
      return errorObj.message;
    }
    if (typeof errorObj.detail === "string") {
      return errorObj.detail;
    }
  }

  return String(error);
}

/**
 * Detect if an error is a Server Action mismatch error
 */
export function isServerActionError(error: unknown): boolean {
  if (!error) return false;

  const errorMessage = getErrorMessage(error);

  // Check for various Server Action error patterns
  const serverActionPatterns = [
    /Server Action.*not found/i,
    /Failed to find Server Action/i,
    /Server Action.*does not exist/i,
    /Action.*\w{40}.*not found/i, // Matches action ID patterns
    /could not find action/i,
  ];

  return serverActionPatterns.some((pattern) => pattern.test(errorMessage));
}

/**
 * Get the current refresh count from sessionStorage
 */
function getRefreshCount(): number {
  if (typeof window === "undefined") return 0;

  try {
    const count = sessionStorage.getItem(REFRESH_COUNT_KEY);
    const timestamp = sessionStorage.getItem(REFRESH_TIMESTAMP_KEY);

    if (!count || !timestamp) return 0;

    // Reset count if last refresh was more than REFRESH_TIMEOUT_MS ago
    const lastRefresh = parseInt(timestamp, 10);
    const now = Date.now();

    if (now - lastRefresh > REFRESH_TIMEOUT_MS) {
      sessionStorage.removeItem(REFRESH_COUNT_KEY);
      sessionStorage.removeItem(REFRESH_TIMESTAMP_KEY);
      return 0;
    }

    return parseInt(count, 10) || 0;
  } catch (error) {
    console.error("Error reading refresh count:", error);
    return 0;
  }
}

/**
 * Increment the refresh count in sessionStorage
 */
function incrementRefreshCount(): void {
  if (typeof window === "undefined") return;

  try {
    const currentCount = getRefreshCount();
    const newCount = currentCount + 1;

    sessionStorage.setItem(REFRESH_COUNT_KEY, String(newCount));
    sessionStorage.setItem(REFRESH_TIMESTAMP_KEY, String(Date.now()));
  } catch (error) {
    console.error("Error incrementing refresh count:", error);
  }
}

/**
 * Check if we should attempt an automatic refresh
 */
function shouldAttemptRefresh(): boolean {
  const refreshCount = getRefreshCount();
  return refreshCount < MAX_REFRESH_ATTEMPTS;
}

/**
 * Perform automatic page refresh with safeguards
 * Returns true if refresh was triggered, false otherwise
 */
function attemptAutoRefresh(): boolean {
  if (typeof window === "undefined") return false;

  if (!shouldAttemptRefresh()) {
    console.warn(
      "Maximum refresh attempts reached. Please manually refresh the page.",
    );
    return false;
  }

  try {
    incrementRefreshCount();

    // Use location.reload() for a clean refresh
    window.location.reload();
    return true;
  } catch (error) {
    console.error("Error during auto-refresh:", error);
    return false;
  }
}

/**
 * Handle Server Action errors with automatic recovery
 * Returns true if error was handled (refresh triggered), false otherwise
 */
export function handleServerActionError(error: unknown): boolean {
  if (!isServerActionError(error)) {
    return false;
  }

  console.error("Server Action error detected:", error);

  // Attempt automatic refresh
  return attemptAutoRefresh();
}
